// Flow Folders Management Modal (WP-07).
// Organizes visual flows and automation campaigns into folders.
import { useState, useEffect, useCallback } from 'react';
import { useI18n } from '../../lib/i18n';
import { cn } from '../../lib/cn';
import {
  flowFolderList,
  flowFolderSave,
  flowFolderDelete,
  type FlowFolderDoc,
} from '../../lib/bots';
import { Button } from '../ui/Button';
import { CloseIcon, PlusIcon, TrashIcon, EditIcon, FormsIcon } from '../icons';
import { useToast } from '../ui/Toast';

interface FlowFoldersModalProps {
  open: boolean;
  onClose: () => void;
}

const PRESET_COLORS = [
  '#4F46E5', // Indigo
  '#10B981', // Emerald
  '#3B82F6', // Blue
  '#F59E0B', // Amber
  '#EC4899', // Pink
  '#8B5CF6', // Purple
  '#14B8A6', // Teal
  '#64748B', // Slate
];

export default function FlowFoldersModal({
  open,
  onClose,
}: FlowFoldersModalProps) {
  const { t } = useI18n();
  const toast = useToast();
  const [folders, setFolders] = useState<FlowFolderDoc[]>([]);
  const [loading, setLoading] = useState(true);

  // Form state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [name, setName] = useState('');
  const [color, setColor] = useState(PRESET_COLORS[0]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Delete state
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const fetchFolders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await flowFolderList({});
      if (res.data?.ok && Array.isArray(res.data.folders)) {
        setFolders(res.data.folders);
      }
    } catch (err) {
      console.error('[FlowFoldersModal] load error', err);
      toast.error((err as Error).message || 'Failed to load flow folders');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchFolders();
      resetForm();
    }
  }, [open, fetchFolders]);

  const resetForm = () => {
    setEditingId(null);
    setIsCreating(false);
    setName('');
    setColor(PRESET_COLORS[0]);
    setError(null);
  };

  const startEdit = (f: FlowFolderDoc) => {
    setEditingId(f.id);
    setIsCreating(true);
    setName(f.name);
    setColor(f.color || PRESET_COLORS[0]);
    setError(null);
  };

  const startCreate = () => {
    resetForm();
    setIsCreating(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError(t('bots.folders.nameRequired') || 'Folder name is required');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const res = await flowFolderSave({
        id: editingId || undefined,
        name: name.trim(),
        color,
      });

      if (res.data?.ok) {
        toast.success(
          editingId
            ? t('bots.folders.updated') || 'Folder updated'
            : t('bots.folders.created') || 'Folder created'
        );
        resetForm();
        fetchFolders();
      }
    } catch (err) {
      console.error('[FlowFoldersModal] save error', err);
      const msg = (err as Error).message || 'Failed to save folder';
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      const res = await flowFolderDelete({ id });
      if (res.data?.ok) {
        toast.success(t('bots.folders.deleted') || 'Folder deleted');
        setFolders((prev) => prev.filter((f) => f.id !== id));
      }
    } catch (err) {
      console.error('[FlowFoldersModal] delete error', err);
      toast.error((err as Error).message || 'Failed to delete folder');
    } finally {
      setDeletingId(null);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="flow-folders-title"
        className="relative w-full max-w-xl rounded-2xl border border-line bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900 overflow-hidden flex flex-col max-h-[90vh]"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-line px-5 py-4 dark:border-slate-800">
          <div>
            <h3 id="flow-folders-title" className="text-base font-bold text-ink dark:text-white">
              {t('bots.folders.title') || 'Flow Folders'}
            </h3>
            <p className="mt-0.5 text-xs text-ink-3 dark:text-slate-400">
              {t('bots.folders.subtitle') ||
                'Organize your visual bot flows, sequences, and campaigns into structured categories.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close') || 'Close'}
            className="rounded-lg p-1 text-ink-3 hover:bg-surface dark:text-slate-400 dark:hover:bg-slate-800"
          >
            <CloseIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4 text-xs">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-ink-2 dark:text-slate-300">
              {folders.length} {t('bots.folders.count') || 'folder(s)'}
            </span>
            {!isCreating && (
              <Button
                size="sm"
                onClick={startCreate}
                leadingIcon={<PlusIcon className="h-4 w-4" />}
              >
                {t('bots.folders.add') || 'New Folder'}
              </Button>
            )}
          </div>

          {/* Create / Edit Form */}
          {isCreating && (
            <form onSubmit={handleSave} className="rounded-2xl border border-brand/30 bg-brand/5 p-4 dark:border-brand/40 dark:bg-brand/10 space-y-3">
              <div className="flex items-center justify-between">
                <span className="font-bold text-ink dark:text-white">
                  {editingId
                    ? t('bots.folders.editTitle') || 'Edit Folder'
                    : t('bots.folders.createTitle') || 'Create New Folder'}
                </span>
                <button
                  type="button"
                  onClick={resetForm}
                  className="text-xs text-ink-3 hover:text-ink dark:text-slate-400"
                >
                  {t('common.cancel') || 'Cancel'}
                </button>
              </div>

              {error && (
                <div className="rounded-lg bg-pink-50 p-2.5 text-danger dark:bg-pink-950/40 dark:text-pink-300">
                  {error}
                </div>
              )}

              <div>
                <label className="block font-medium text-ink-2 dark:text-slate-300 mb-1">
                  {t('bots.folders.name') || 'Folder Name'} *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('bots.folder.namePlaceholder') || 'e.g. Marketing Campaigns'}
                  required
                  className="w-full rounded-lg border border-line bg-white px-3 py-1.5 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                />
              </div>

              <div>
                <label className="block font-medium text-ink-2 dark:text-slate-300 mb-1.5">
                  {t('bots.folders.color') || 'Color Tag'}
                </label>
                <div className="flex flex-wrap items-center gap-2">
                  {PRESET_COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setColor(c)}
                      className={cn(
                        'h-6 w-6 rounded-full transition-transform',
                        color === c ? 'scale-125 ring-2 ring-brand ring-offset-2 dark:ring-offset-slate-900' : 'hover:scale-110'
                      )}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-1">
                <Button variant="secondary" size="xs" onClick={resetForm} disabled={saving}>
                  {t('common.cancel') || 'Cancel'}
                </Button>
                <Button size="xs" type="submit" loading={saving}>
                  {editingId ? t('common.save') || 'Save' : t('common.create') || 'Create'}
                </Button>
              </div>
            </form>
          )}

          {/* Folders List */}
          {loading && folders.length === 0 ? (
            <div className="grid h-36 place-items-center">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-brand border-t-transparent" />
            </div>
          ) : folders.length === 0 ? (
            <div className="rounded-xl border border-dashed border-line p-8 text-center text-ink-3 dark:border-slate-800 dark:text-slate-400">
              {t('bots.folders.empty') || 'No folders created yet.'}
            </div>
          ) : (
            <div className="divide-y divide-line rounded-xl border border-line dark:divide-slate-800 dark:border-slate-800">
              {folders.map((f) => (
                <div
                  key={f.id}
                  className="flex items-center justify-between p-3.5 hover:bg-surface/40 dark:hover:bg-slate-800/40"
                >
                  <div className="flex items-center gap-3">
                    <span
                      className="grid h-8 w-8 place-items-center rounded-lg text-white bg-brand"
                      style={f.color ? { backgroundColor: f.color } : undefined}
                    >
                      <FormsIcon className="h-4 w-4" />
                    </span>
                    <span className="font-semibold text-ink dark:text-white">{f.name}</span>
                  </div>

                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => startEdit(f)}
                      aria-label={t('common.edit') || 'Edit'}
                      className="rounded p-1 text-ink-3 hover:bg-surface hover:text-ink dark:text-slate-400 dark:hover:bg-slate-800"
                    >
                      <EditIcon className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(f.id)}
                      disabled={deletingId === f.id}
                      aria-label={t('common.delete') || 'Delete'}
                      className="rounded p-1 text-ink-3 hover:bg-danger/10 hover:text-danger dark:text-slate-400"
                    >
                      <TrashIcon className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end border-t border-line px-5 py-3 dark:border-slate-800">
          <Button variant="secondary" size="sm" onClick={onClose}>
            {t('common.close') || 'Close'}
          </Button>
        </div>
      </div>
    </div>
  );
}
